import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ListGroup,
  Badge,
  Spinner,
  Alert,
  Form,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AuthContext from "../contexts/AuthContext";
import { toast, Toaster } from "react-hot-toast";
import api from "../api";

const SharedWorksheets = () => {
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch subjects and public worksheets when component mounts
  useEffect(() => {
    console.log("SharedWorksheets component mounted");
    fetchSubjects();
    fetchPublicWorksheets();
  }, []);

  // When subject filter changes, fetch filtered worksheets
  useEffect(() => {
    fetchPublicWorksheets(true);
  }, [selectedSubject]);

  const fetchSubjects = async () => {
    try {
      const response = await api.get("/api/csv-data/subjects/");
      if (response.data && Array.isArray(response.data)) {
        setSubjects(response.data);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchPublicWorksheets = async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setLoading(true);
    }
    setError("");

    try {
      console.log(
        "Fetching public worksheets",
        selectedSubject ? `for subject: ${selectedSubject}` : "for all subjects"
      );

      // Build the URL with params
      let url = "/api/worksheets/public/";
      const params = new URLSearchParams();

      if (selectedSubject) {
        params.append("subject", selectedSubject);
      }

      // Add the params to the URL if there are any
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await api.get(url);
      console.log("Public worksheets response:", response);

      if (response.data && Array.isArray(response.data)) {
        console.log("Setting public worksheets array:", response.data);
        setWorksheets(response.data);
      } else if (
        response.data &&
        typeof response.data === "object" &&
        Array.isArray(response.data.results)
      ) {
        // Handle DRF pagination format
        console.log(
          "Setting public worksheets from results:",
          response.data.results
        );
        setWorksheets(response.data.results);
      } else {
        console.warn(
          "API did not return an array for public worksheets:",
          response.data
        );
        setWorksheets([]);
      }
    } catch (error) {
      console.error("Error fetching public worksheets:", error);
      if (error.response) {
        console.error(
          "Error response:",
          error.response.status,
          error.response.data
        );
      }
      setError("Failed to fetch public worksheets. Please try again later.");
      setWorksheets([]);
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      }
    }
  };

  const handleLoadWorksheet = async (worksheet) => {
    setIsLoading(true);
    setError("");

    try {
      console.log("Attempting to load shared worksheet:", worksheet.id);

      // First verify that this worksheet still exists and is public
      const checkResponse = await api.get(
        `/api/worksheets/${worksheet.id}/questions/`
      );
      console.log("Worksheet check successful:", checkResponse.data);

      // Navigate to worksheet generator with worksheet ID in state
      navigate("/worksheets", {
        state: {
          worksheetId: worksheet.id,
          isPublic: true,
          ownerId: worksheet.user_id,
        },
      });

      // Don't set isLoading to false here, as we're navigating away
    } catch (error) {
      console.error("Error preparing to load worksheet:", error);
      let errorMessage = "Failed to load the worksheet. Please try again.";

      if (error.response) {
        console.error(
          "Response error:",
          error.response.status,
          error.response.data
        );

        if (error.response.status === 403) {
          errorMessage =
            "This worksheet is no longer public or you don't have permission to view it.";
        } else if (error.response.status === 404) {
          errorMessage = "This worksheet no longer exists.";
        }
      }

      setError(errorMessage);
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Toaster position="top-right" />
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading shared worksheets...</p>
      </Container>
    );
  }

  // Make sure worksheets is an array before rendering
  const worksheetsArray = Array.isArray(worksheets) ? worksheets : [];

  return (
    <Container className="my-4">
      <Toaster position="top-right" />
      <Row className="mb-4">
        <Col>
          <h2>Shared Worksheets</h2>
          <p>
            Browse worksheets shared by the community. You can view and use
            these worksheets but cannot modify them unless you are the owner.
          </p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Filter by Subject</Form.Label>
            <Form.Select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="">All Subjects</option>
              {subjects.map((subject, idx) => (
                <option key={idx} value={subject}>
                  {subject}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6} className="d-flex align-items-end justify-content-end">
          <Button
            variant="outline-primary"
            onClick={() => {
              // Force a refresh of the worksheet list
              fetchPublicWorksheets(true);
              toast.success("Refreshing shared worksheets...");
            }}
          >
            Refresh Worksheets
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {worksheetsArray.length === 0 ? (
        <Card className="text-center p-5">
          <Card.Body>
            <Card.Title>No Shared Worksheets</Card.Title>
            <Card.Text>
              There are no publicly shared worksheets available at the moment.
              You can share your own worksheets from the "My Worksheets"
              section.
            </Card.Text>
            <Button
              variant="primary"
              onClick={() => navigate("/my-worksheets", { replace: true })}
            >
              Go to My Worksheets
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {worksheetsArray.map((worksheet) => (
            <Col md={6} lg={4} key={worksheet.id} className="mb-4">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-start">
                  <h5 className="mb-0">{worksheet.title}</h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <Badge bg="primary" className="me-2">
                      {worksheet.subject}
                    </Badge>
                    {worksheet.topic && (
                      <Badge bg="secondary" className="me-2">
                        {worksheet.topic}
                      </Badge>
                    )}
                    <Badge bg="info">{worksheet.difficulty}</Badge>
                  </div>

                  <p>
                    <small className="text-muted">
                      Shared by: {worksheet.user_username}
                    </small>
                  </p>

                  {worksheet.description && (
                    <Card.Text className="text-muted">
                      {worksheet.description}
                    </Card.Text>
                  )}

                  <div className="d-flex justify-content-between mt-3">
                    <small className="text-muted">
                      Created:{" "}
                      {new Date(worksheet.created_at).toLocaleDateString()}
                    </small>
                    <small className="text-muted">
                      {(() => {
                        try {
                          // Handle both string and array formats for backward compatibility
                          let codes;
                          if (typeof worksheet.question_codes === "string") {
                            codes = JSON.parse(worksheet.question_codes);
                          } else {
                            codes = worksheet.question_codes;
                          }
                          return Array.isArray(codes) ? codes.length : 0;
                        } catch (e) {
                          console.error(
                            "Error parsing question_codes:",
                            e,
                            worksheet.question_codes
                          );
                          return 0;
                        }
                      })()}{" "}
                      questions
                    </small>
                  </div>
                </Card.Body>
                <Card.Footer>
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={() => handleLoadWorksheet(worksheet)}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "View Worksheet"}
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default SharedWorksheets;
